<template>
  <SuperAdminShell>
    <div class="super-admin-page sa-reports">
      <ReportsHeader
        :is-mock="isMock"
        :export-disabled="!filteredRows.length"
        :exporting="exporting"
        @export="exportCsv"
      />

      <div class="super-admin-page__shell sa-reports__shell">
        <div class="sa-reports__container">
          <div v-if="loadError && !hasReady" class="sa-reports__state sa-reports__state--error" role="alert">
            <h2>Não foi possível carregar os relatórios</h2>
            <p>Tente novamente em instantes. Se o problema continuar, verifique sua conexão.</p>
            <button type="button" class="btnSave" @click="loadActiveReport">Tentar novamente</button>
          </div>

          <div v-else-if="!hasReady" class="sa-reports__state">
            <p>Carregando relatórios...</p>
          </div>

          <template v-else>
            <ReportsTabs v-model="activeTab" :tabs="tabs" />

            <div
              :id="`report-panel-${activeTab}`"
              role="tabpanel"
              :aria-labelledby="`report-tab-${activeTab}`"
              class="sa-reports__panel"
            >
              <ReportsSummary :metrics="summaryMetrics" />

              <ReportsCharts
                v-if="!loadingTab && !loadError && filteredRows.length"
                :tab="activeTab"
                :rows="filteredRows"
              />

              <ReportsFilters
                :filters="filters"
                :show-period="activeTab === 'financial'"
                :active-chips="activeChips"
                :has-active-filters="hasActiveFilters"
                @update:filters="onFiltersUpdate"
                @clear="clearFilters"
                @remove-chip="removeChip"
              />

              <div class="sa-reports__results">
                <p class="sa-reports__results-count">
                  {{ resultsLabel }}
                </p>
              </div>

              <div v-if="loadingTab" class="sa-reports__state">
                <p>Atualizando relatório...</p>
              </div>

              <div v-else-if="loadError" class="sa-reports__state sa-reports__state--error" role="alert">
                <h2>Não foi possível atualizar o relatório</h2>
                <p>Tente novamente em instantes.</p>
                <button type="button" class="btnSave" @click="loadActiveReport">Tentar novamente</button>
              </div>

              <div v-else-if="!filteredRows.length" class="sa-reports__empty">
                <h3>Nenhuma empresa encontrada</h3>
                <p>Altere os filtros para encontrar outros resultados.</p>
                <button type="button" class="btnSave" @click="clearFilters">Limpar filtros</button>
              </div>

              <template v-else>
                <ClientsReportTable
                  v-if="activeTab === 'clients'"
                  :rows="paginatedRows"
                  :sort-key="sortKey"
                  :sort-dir="sortDir"
                  @sort="toggleSort"
                />
                <FinancialReportTable
                  v-else-if="activeTab === 'financial'"
                  :rows="paginatedRows"
                  :sort-key="sortKey"
                  :sort-dir="sortDir"
                  @sort="toggleSort"
                />
                <SalesReportTable
                  v-else
                  :rows="paginatedRows"
                  :sort-key="sortKey"
                  :sort-dir="sortDir"
                  @sort="toggleSort"
                />

                <ReportsPagination
                  :page="page"
                  :total-pages="totalPages"
                  :page-size="pageSize"
                  @update:page="page = $event"
                  @update:pageSize="onPageSizeChange"
                />
              </template>
            </div>
          </template>
        </div>
      </div>
    </div>
  </SuperAdminShell>
</template>

<script>
import { mapState } from 'vuex';
import SuperAdminShell from '@/Views/Weflix/SuperAdminShell.vue';
import superAdminApi from '@/utils/superAdmin';
import {
  businessName,
  formatCurrencyBRL,
  formatNumberBR,
  systemName,
} from '@/utils/superAdmin/formatters';
import ReportsHeader from '@/components/Weflix/SuperAdmin/Reports/ReportsHeader.vue';
import ReportsTabs from '@/components/Weflix/SuperAdmin/Reports/ReportsTabs.vue';
import ReportsSummary from '@/components/Weflix/SuperAdmin/Reports/ReportsSummary.vue';
import ReportsFilters from '@/components/Weflix/SuperAdmin/Reports/ReportsFilters.vue';
import ReportsPagination from '@/components/Weflix/SuperAdmin/Reports/ReportsPagination.vue';
import ReportsCharts from '@/components/Weflix/SuperAdmin/Reports/ReportsCharts.vue';
import ClientsReportTable from '@/components/Weflix/SuperAdmin/Reports/ClientsReportTable.vue';
import FinancialReportTable from '@/components/Weflix/SuperAdmin/Reports/FinancialReportTable.vue';
import SalesReportTable from '@/components/Weflix/SuperAdmin/Reports/SalesReportTable.vue';

const TAB_IDS = ['clients', 'financial', 'coursesSales'];
const DEFAULT_SORT = {
  clients: { key: 'revenue', dir: 'desc' },
  financial: { key: 'net', dir: 'desc' },
  coursesSales: { key: 'sales', dir: 'desc' },
};

export default {
  name: 'SuperAdminReports',
  components: {
    SuperAdminShell,
    ReportsHeader,
    ReportsTabs,
    ReportsSummary,
    ReportsFilters,
    ReportsPagination,
    ReportsCharts,
    ClientsReportTable,
    FinancialReportTable,
    SalesReportTable,
  },
  data() {
    return {
      hasReady: false,
      loadingTab: false,
      loadError: false,
      isMock: false,
      exporting: false,
      activeTab: 'clients',
      page: 1,
      pageSize: 20,
      sortKey: DEFAULT_SORT.clients.key,
      sortDir: DEFAULT_SORT.clients.dir,
      syncingQuery: false,
      skipNextFetch: false,
      fetchRequestId: 0,
      searchDebounceTimer: null,
      tabs: [
        { id: 'clients', label: 'Clientes' },
        { id: 'financial', label: 'Financeiro' },
        { id: 'coursesSales', label: 'Cursos e Vendas' },
      ],
      filters: {
        typeSystem: '',
        typeBusiness: '',
        search: '',
        period: '',
      },
      reports: {
        clients: [],
        financial: [],
        coursesSales: [],
      },
    };
  },
  computed: {
    ...mapState(['token']),
    filteredRows() {
      if (this.activeTab === 'financial') return this.reports.financial || [];
      if (this.activeTab === 'coursesSales') return this.reports.coursesSales || [];
      return this.reports.clients || [];
    },
    sortedRows() {
      const rows = [...this.filteredRows];
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const av = a?.[key];
        const bv = b?.[key];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'string' || typeof bv === 'string') {
          return String(av).localeCompare(String(bv), 'pt-BR', { sensitivity: 'base' }) * dir;
        }
        return (Number(av) - Number(bv)) * dir;
      });
      return rows;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.sortedRows.length / this.pageSize));
    },
    paginatedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.sortedRows.slice(start, start + this.pageSize);
    },
    hasActiveFilters() {
      return !!(
        this.filters.typeSystem
        || this.filters.typeBusiness
        || (this.filters.search || '').trim()
        || (this.activeTab === 'financial' && this.filters.period)
      );
    },
    activeChips() {
      const chips = [];
      if (this.filters.typeSystem === '1') chips.push({ key: 'typeSystem', label: 'Weeduc' });
      if (this.filters.typeSystem === '2') chips.push({ key: 'typeSystem', label: 'Weflix' });
      if (this.filters.typeBusiness === '1') chips.push({ key: 'typeBusiness', label: 'Escolar' });
      if (this.filters.typeBusiness === '2') chips.push({ key: 'typeBusiness', label: 'Corporativo' });
      if ((this.filters.search || '').trim()) {
        chips.push({ key: 'search', label: this.filters.search.trim() });
      }
      if (this.activeTab === 'financial' && this.filters.period) {
        const [year, month] = this.filters.period.split('-');
        const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
        const label = month && year
          ? `${monthNames[Number(month) - 1] || month} ${year}`
          : this.filters.period;
        chips.push({ key: 'period', label });
      }
      return chips;
    },
    resultsLabel() {
      const count = this.filteredRows.length;
      if (this.activeTab === 'financial') {
        return `${formatNumberBR(count)} ${count === 1 ? 'resultado' : 'resultados'}`;
      }
      return `${formatNumberBR(count)} ${count === 1 ? 'empresa encontrada' : 'empresas encontradas'}`;
    },
    summaryMetrics() {
      const rows = this.filteredRows;
      if (this.activeTab === 'financial') {
        const revenue = rows.reduce((sum, row) => sum + Number(row.revenue || 0), 0);
        const fees = rows.reduce((sum, row) => sum + Number(row.fees || 0), 0);
        const net = rows.reduce((sum, row) => sum + Number(row.net || 0), 0);
        return [
          { label: 'Receita', value: formatCurrencyBRL(revenue) },
          { label: 'Taxas', value: formatCurrencyBRL(fees) },
          { label: 'Líquido', value: formatCurrencyBRL(net) },
          { label: 'Empresas', value: formatNumberBR(rows.length) },
        ];
      }
      if (this.activeTab === 'coursesSales') {
        const courses = rows.reduce((sum, row) => sum + Number(row.courses || 0), 0);
        const sales = rows.reduce((sum, row) => sum + Number(row.sales || 0), 0);
        const weighted = rows.reduce(
          (sum, row) => sum + Number(row.avgTicket || 0) * Number(row.sales || 0),
          0,
        );
        const ticket = sales > 0 ? weighted / sales : 0;
        return [
          { label: 'Cursos', value: formatNumberBR(courses) },
          { label: 'Vendas', value: formatNumberBR(sales) },
          { label: 'Ticket médio', value: formatCurrencyBRL(ticket), hint: 'Ponderado por vendas' },
          { label: 'Empresas', value: formatNumberBR(rows.length) },
        ];
      }
      const students = rows.reduce((sum, row) => sum + Number(row.students || 0), 0);
      const courses = rows.reduce((sum, row) => sum + Number(row.courses || 0), 0);
      const revenue = rows.reduce((sum, row) => sum + Number(row.revenue || 0), 0);
      return [
        { label: 'Empresas', value: formatNumberBR(rows.length) },
        { label: 'Alunos / colaboradores', value: formatNumberBR(students) },
        { label: 'Cursos', value: formatNumberBR(courses) },
        { label: 'Faturamento', value: formatCurrencyBRL(revenue) },
      ];
    },
  },
  watch: {
    activeTab(tab, prev) {
      if (tab === prev) return;
      const defaults = DEFAULT_SORT[tab] || DEFAULT_SORT.clients;
      this.sortKey = defaults.key;
      this.sortDir = defaults.dir;
      this.page = 1;
      this.syncQuery();
      if (!this.skipNextFetch) {
        this.scheduleActiveReportFetch({ immediate: true });
      }
    },
    filters: {
      deep: true,
      handler() {
        this.page = 1;
        this.syncQuery();
        if (!this.skipNextFetch) {
          this.scheduleActiveReportFetch();
        }
      },
    },
    page() {
      this.syncQuery();
    },
    pageSize() {
      this.syncQuery();
    },
    sortKey() {
      this.syncQuery();
    },
    sortDir() {
      this.syncQuery();
    },
    filteredRows() {
      if (this.page > this.totalPages) {
        this.page = this.totalPages;
      }
    },
    '$route.query': {
      handler() {
        if (this.syncingQuery) return;
        this.skipNextFetch = true;
        this.applyQuery(this.$route.query);
        this.$nextTick(() => {
          this.skipNextFetch = false;
          this.scheduleActiveReportFetch({ immediate: true });
        });
      },
    },
  },
  methods: {
    onFiltersUpdate(next) {
      const patched = { ...next };
      if (patched.typeSystem === '2') {
        patched.typeBusiness = '';
      }
      this.filters = patched;
    },
    clearFilters() {
      this.filters = {
        typeSystem: '',
        typeBusiness: '',
        search: '',
        period: '',
      };
    },
    removeChip(key) {
      if (key === 'typeSystem') this.filters = { ...this.filters, typeSystem: '', typeBusiness: '' };
      else if (key === 'typeBusiness') this.filters = { ...this.filters, typeBusiness: '' };
      else if (key === 'search') this.filters = { ...this.filters, search: '' };
      else if (key === 'period') this.filters = { ...this.filters, period: '' };
    },
    toggleSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = ['name', 'period', 'createdAt'].includes(key) ? 'asc' : 'desc';
      }
      this.page = 1;
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
    },
    applyQuery(query = {}) {
      const tab = TAB_IDS.includes(query.tab) ? query.tab : this.activeTab;
      this.activeTab = tab;

      const defaults = DEFAULT_SORT[tab] || DEFAULT_SORT.clients;
      this.sortKey = query.sort && typeof query.sort === 'string' ? query.sort : defaults.key;
      this.sortDir = query.dir === 'asc' || query.dir === 'desc' ? query.dir : defaults.dir;

      const page = Number(query.page);
      this.page = Number.isFinite(page) && page > 0 ? page : 1;

      const pageSize = Number(query.pageSize);
      this.pageSize = [10, 20, 50].includes(pageSize) ? pageSize : 20;

      this.filters = {
        typeSystem: query.system === '1' || query.system === '2' ? query.system : '',
        typeBusiness: query.type === '1' || query.type === '2' ? query.type : '',
        search: typeof query.q === 'string' ? query.q : '',
        period: typeof query.period === 'string' ? query.period : '',
      };

      if (this.filters.typeSystem === '2') {
        this.filters.typeBusiness = '';
      }
    },
    syncQuery() {
      if (!this.$router) return;
      this.syncingQuery = true;
      const query = {};
      if (this.activeTab !== 'clients') query.tab = this.activeTab;
      if (this.filters.typeSystem) query.system = this.filters.typeSystem;
      if (this.filters.typeBusiness) query.type = this.filters.typeBusiness;
      if ((this.filters.search || '').trim()) query.q = this.filters.search.trim();
      if (this.activeTab === 'financial' && this.filters.period) query.period = this.filters.period;
      if (this.page > 1) query.page = String(this.page);
      if (this.pageSize !== 20) query.pageSize = String(this.pageSize);

      const defaults = DEFAULT_SORT[this.activeTab] || DEFAULT_SORT.clients;
      if (this.sortKey !== defaults.key) query.sort = this.sortKey;
      if (this.sortDir !== defaults.dir) query.dir = this.sortDir;

      this.$router.replace({ query }).catch(() => {}).finally(() => {
        this.$nextTick(() => {
          this.syncingQuery = false;
        });
      });
    },
    scheduleActiveReportFetch({ immediate = false } = {}) {
      if (this.skipNextFetch) return;
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      if (immediate) {
        this.loadActiveReport();
        return;
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounceTimer = null;
        this.loadActiveReport();
      }, 300);
    },
    buildActiveFilters() {
      return {
        typeSystem: this.filters.typeSystem || undefined,
        typeBusiness: this.filters.typeSystem === '2' ? undefined : (this.filters.typeBusiness || undefined),
        search: (this.filters.search || '').trim() || undefined,
        period: this.activeTab === 'financial' ? (this.filters.period || undefined) : undefined,
      };
    },
    async loadActiveReport() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }

      const requestId = ++this.fetchRequestId;
      const tab = this.activeTab;
      const filters = this.buildActiveFilters();
      const isInitial = !this.hasReady;

      this.loadError = false;
      this.loadingTab = !isInitial;

      try {
        let rows = [];
        if (tab === 'financial') {
          rows = await superAdminApi.getReportFinancial(this.token, filters);
        } else if (tab === 'coursesSales') {
          rows = await superAdminApi.getReportCoursesSales(this.token, filters);
        } else {
          rows = await superAdminApi.getReportClients(this.token, filters);
        }

        if (requestId !== this.fetchRequestId) return;

        const list = Array.isArray(rows) ? rows : [];
        if (tab === 'financial') {
          this.reports = { ...this.reports, financial: list };
        } else if (tab === 'coursesSales') {
          this.reports = { ...this.reports, coursesSales: list };
        } else {
          this.reports = { ...this.reports, clients: list };
        }
        this.isMock = false;
        this.hasReady = true;
        this.loadingTab = false;
      } catch (error) {
        if (requestId !== this.fetchRequestId) return;
        console.error('Erro ao carregar relatórios Super Admin:', error);
        this.loadError = true;
        this.loadingTab = false;
        if (!isInitial) {
          this.hasReady = true;
        }
      }
    },
    exportCsv() {
      const rows = this.filteredRows;
      if (!rows.length || this.exporting) return;

      this.exporting = true;
      try {
        let headers = [];
        let mapRow = null;
        let fileSlug = 'clientes';

        if (this.activeTab === 'clients') {
          fileSlug = 'clientes';
          headers = ['ID', 'Empresa', 'Sistema', 'Tipo', 'Alunos', 'Cursos', 'Vendas', 'Faturamento', 'Criada em'];
          mapRow = (row) => [
            row.id,
            row.name,
            systemName(row.typeSystem),
            businessName(row),
            row.students,
            row.courses,
            row.sales,
            row.revenue,
            row.createdAt,
          ];
        } else if (this.activeTab === 'financial') {
          fileSlug = 'financeiro';
          headers = ['ID', 'Empresa', 'Sistema', 'Tipo', 'Período', 'Receita', 'Taxas', 'Líquido'];
          mapRow = (row) => [
            row.id,
            row.name,
            systemName(row.typeSystem),
            businessName(row),
            row.period,
            row.revenue,
            row.fees,
            row.net,
          ];
        } else {
          fileSlug = 'cursos-vendas';
          headers = ['ID', 'Empresa', 'Sistema', 'Tipo', 'Cursos', 'Vendas', 'Ticket médio'];
          mapRow = (row) => [
            row.id,
            row.name,
            systemName(row.typeSystem),
            businessName(row),
            row.courses,
            row.sales,
            row.avgTicket,
          ];
        }

        const escapeCell = (cell) => {
          const text = String(cell ?? '');
          if (/[",\n;]/.test(text)) {
            return `"${text.replace(/"/g, '""')}"`;
          }
          return text;
        };

        const csv = [headers, ...rows.map(mapRow)]
          .map((line) => line.map(escapeCell).join(';'))
          .join('\n');

        const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `relatorio-${fileSlug}-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } finally {
        this.exporting = false;
      }
    },
  },
  created() {
    this.skipNextFetch = true;
    this.applyQuery(this.$route.query);
    this.$nextTick(() => {
      this.skipNextFetch = false;
      this.loadActiveReport();
    });
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
};
</script>

<style scoped>
.sa-reports {
  --dashboard-bg: #f6f7fb;
  --dashboard-card: #ffffff;
  --dashboard-border: #e8eaf0;
  --dashboard-text: #191c24;
  --dashboard-muted: #697386;
  --dashboard-primary: #250891;
  --dashboard-secondary: #1ac7d0;
  background: var(--dashboard-bg);
}

.sa-reports__shell {
  background: var(--dashboard-bg);
}

.sa-reports__container {
  width: 100%;
  max-width: none;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.15rem;
  background: var(--dashboard-card);
  border: 1px solid var(--dashboard-border);
  border-radius: 14px;
  padding: 1.15rem 1.25rem 1.35rem;
  box-sizing: border-box;
}

.sa-reports__panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}

.sa-reports__results {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.sa-reports__results-count {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--dashboard-muted);
}

.sa-reports__empty,
.sa-reports__state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: var(--dashboard-muted);
}

.sa-reports__empty h3,
.sa-reports__state--error h2 {
  margin: 0 0 0.4rem;
  color: var(--dashboard-text);
  font-size: 1.05rem;
}

.sa-reports__empty p,
.sa-reports__state--error p {
  margin: 0 0 1rem;
  font-size: 0.875rem;
}

@media (max-width: 768px) {
  .sa-reports__container {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>
